<template>
  <PageSection font="medium">
    <SectionGroup gap="medium">
      <SectionBox>
        <BasicSection appearance="heading">
          <PageTitle :title="seoHeader || 'Работа и вакансии'">
            <HotPointHint
              v-if="hint && mediaQueries.mobile"
              @close="markHintAsRead"
              with-border
              class="vacancy-list-page__hint"
            >
              <template #title>{{ hint.title }}</template>
              <template #default>
                {{ hint.description }}
              </template>
            </HotPointHint>

            <template v-if="!hasSuggestedFilters">
              <BasicButton
                v-if="mediaQueries.mobile"
                appearance="pseudo-link"
                size="xl"
                @click="onSubscriptionClick"
              >
                <span>Подписаться на вакансии</span>
              </BasicButton>
              <ButtonGroup v-else class="vacancy-list-page__button-group">
                <HotPointHint
                  v-if="hint && !mediaQueries.mobile"
                  @close="markHintAsRead"
                  with-border
                  class="vacancy-list-page__hint"
                >
                  <template #title>{{ hint.title }}</template>
                  <template #default>
                    {{ hint.description }}
                  </template>
                </HotPointHint>

                <BasicButton
                  v-if="!alias"
                  appearance="primary"
                  size="m"
                  @click="onSubscriptionClick"
                >
                  <span>Подписаться на вакансии</span>
                </BasicButton>
                <BasicButton
                  v-if="!alias"
                  :href="rssLink"
                  size="m"
                  appearance="tertiary"
                  rel="nofollow"
                  >RSS</BasicButton
                >
                <BasicButton
                  v-if="showSeoButton"
                  :href="getAddSeoLink"
                  appearance="tertiary"
                  size="m"
                  >Добавить набор фильтров</BasicButton
                >
                <BasicButton
                  v-if="showUpdateSeoButton"
                  :href="getUpdateSeoFilterLink"
                  appearance="tertiary"
                  size="m"
                  >Обновить фильтр</BasicButton
                >
              </ButtonGroup>
            </template>
          </PageTitle>
        </BasicSection>

        <BasicTabs v-if="tabs" :tabs="tabs" appearance="primary" />

        <BasicSection appearance="search-panel">
          <SearchPanel
            :indicator-visible="hasAnyFilters"
            :indicator-appearance="indicatorAppearance"
            @openFilters="openModalFilters"
          >
            <template #primary>
              <SearchInput
                v-model="state.query"
                @update:modelValue="scheduleListUpdate"
                type="search"
                placeholder="Поиск"
              />
            </template>
            <template #secondary>
              <BasicSelect
                v-model="state.order"
                @update:modelValue="scheduleListUpdate"
                :options="filters.options.order"
              />
            </template>
          </SearchPanel>

          <TransitionExpand v-if="!mediaQueries.mobile">
            <SearchFilters v-if="hasAnyFilters" :key="hasCustomizedFilters">
              <SectionGroup gap="medium">
                <SelectedFilters
                  v-if="hasSuggestedFilters"
                  :items="suggestedFilters.selectedList"
                  title="Фильтры на основе вашего профиля:"
                />

                <SelectedFilters
                  v-if="hasCustomizedFilters"
                  :items="filters.selectedList"
                  title="Выбранные вами фильтры:"
                  :max="2"
                  @remove="filters.resetOne($event), scheduleListUpdate()"
                />

                <FiltersControls
                  v-if="hasCustomizedFilters"
                  appearance="search-panel"
                  @save="saveFilters(filters.items)"
                  @reset="filters.reset(), scheduleListUpdate()"
                />
              </SectionGroup>
            </SearchFilters>
          </TransitionExpand>

          <TransitionExpand>
            <SearchTotal
              v-if="currentVacancies.list.length !== 0"
              v-model="searchResultsTitle"
              appearance="search-panel"
            />
          </TransitionExpand>
          <SocialLinks
            v-if="filters.socialLinks"
            :title="filters.socialLinks.title"
            :links="filters.socialLinks.links"
          />
        </BasicSection>
      </SectionBox>

      <ResourceAction
        ref="modalFiltersAction"
        :action="action"
        :debounce="400"
        @success="onModalRequestSuccess"
        v-slot="{ isLoading: isModalLoading }"
      >
        <PaginatedResource
          ref="listResource"
          v-model="currentVacancies"
          :action="action"
          :params="params"
          :debounce="400"
          @success="onListRequestSuccess"
          v-slot="{ isLoading }"
        >
          <TransitionExpand>
            <WithPagination
              :key="isLoading"
              ref="scrollTarget"
              :params="mappedParams"
              @click="onPagination"
            >
              <SectionGroup v-if="isLoading || isModalLoading" gap="medium">
                <SectionBox
                  v-for="(_, index) in Array(
                    currentVacancies.list.length < 3
                      ? 3
                      : currentVacancies.list.length,
                  )"
                  :key="index"
                >
                  <BasicSection appearance="horizontal">
                    <VacancyCardSkeleton />
                  </BasicSection>
                </SectionBox>
              </SectionGroup>
              <SectionGroup
                v-else-if="currentVacancies.list.length === 0"
                gap="medium"
              >
                <SectionBox>
                  <BasicSection>
                    <NoContent
                      :title="$options.noContent.title"
                      :description="$options.noContent.description"
                      :icon="$options.noContent.icon"
                      :actions="$options.noContent.actions"
                    />
                  </BasicSection>
                </SectionBox>
                <QuickResponseVacancies
                  v-if="quickResponseVacanciesList.length"
                  :vacancies="quickResponseVacanciesList"
                  :show-feedback="quickResponseFeedbackEnabled"
                  @hide-feedback="quickResponseFeedbackEnabled = false"
                />
              </SectionGroup>
              <SectionGroup
                v-else-if="currentVacancies.list.length > 0"
                gap="medium"
              >
                <template v-for="(vacancy, index) in currentVacancies.list">
                  <SkillBanner
                    v-if="index === 7 && skillsBanner"
                    :quiz-id="skillsBanner.quizId"
                    :key="index"
                  />
                  <SectionBox
                    :key="vacancy.id"
                    :appearance="vacancy.isMarked ? 'marked' : null"
                  >
                    <BasicSection appearance="horizontal">
                      <VacancyCard :vacancy="vacancy" />
                    </BasicSection>
                  </SectionBox>
                  <QuickResponseVacancies
                    v-if="showQuickResponse(index)"
                    :key="`quick-response-vacancies-${vacancy.id}`"
                    :vacancies="quickResponseVacanciesList"
                    :show-feedback="quickResponseFeedbackEnabled"
                    :is-open="!hasCustomizedFilters"
                    @hide-feedback="quickResponseFeedbackEnabled = false"
                  />
                </template>
              </SectionGroup>
            </WithPagination>
          </TransitionExpand>
        </PaginatedResource>
      </ResourceAction>

      <SectionBox v-if="similarVacanciesVisible">
        <BasicSection>
          <ContentSection appearance="form-section" title="Похожие вакансии">
            <BasicSlider
              v-if="mediaQueries.mobile"
              ref="slider"
              :slides="similarVacancies"
              pagination-under
              :show-pagination="false"
              :show-navigation="false"
              :space-between="16"
              v-slot="{ slide: similarVacancy }"
            >
              <BasicLink
                appearance="dark"
                :key="similarVacancy.href"
                :href="similarVacancy.href"
                :title="similarVacancy.title"
              />
            </BasicSlider>
            <BasicColumns v-else :columns="2" appearance="responsive">
              <BasicLink
                v-for="similarVacancy in similarVacancies"
                appearance="dark"
                :key="similarVacancy.href"
                :href="similarVacancy.href"
                :title="similarVacancy.title"
              />
            </BasicColumns>
          </ContentSection>
        </BasicSection>
      </SectionBox>
    </SectionGroup>

    <TransitionFade body-scroll-lock>
      <VacanciesSubscriptionModal
        v-if="subscriptionsModalVisible"
        :query="state.query"
        :filters="filters"
        :params="params"
        :subscriptions.sync="state.subscriptions"
        @close="subscriptionsModalVisible = false"
      />
    </TransitionFade>

    <VuePortal target="[data-vacancy-list-sidebar]">
      <ListPageSidebar v-if="!mediaQueries.mobile">
        <template #beforeFilters>
          <PageSection v-if="!visitor.isGuest" font="medium">
            <SectionBox>
              <BasicSection appearance="sidebar-filters">
                <CollapsibleSection
                  with-title-hover
                  title="Сохранённые фильтры"
                  :counter="savedFilters.items.length"
                >
                  <SavedFiltersWidget @click="loadSavedFilter" />
                </CollapsibleSection>
              </BasicSection>
            </SectionBox>
          </PageSection>
        </template>
        <template #filters>
          <VacancyFilters
            :filters="filters.items"
            :options="filters.options"
            :dynamic-options="filters.dynamicOptions"
            :groups="groups"
            @change="scheduleListUpdate"
          >
            <template #controls>
              <FiltersControls
                v-if="hasCustomizedFilters"
                @save="saveFilters(filters.items)"
                @reset="filters.reset(), scheduleListUpdate()"
              />
            </template>
          </VacancyFilters>
        </template>
      </ListPageSidebar>
    </VuePortal>
    <VuePortal target="[data-subscribe]">
      <PageSection>
        <NewsSubscribe :show-subscribe="showSubscribe" />
      </PageSection>
    </VuePortal>

    <TransitionFade body-scroll-lock>
      <FiltersModal
        v-if="filtersModalVisible"
        :saved-filters-description="$options.savedFiltersDescription"
        @close="closeModalFilters"
        @loadSavedFilter="loadSavedFilter($event), closeModalFilters()"
        @saveFilters="saveFilters"
      >
        <template #filters="{ filters: items, options, dynamicOptions }">
          <VacancyFilters
            :filters="items"
            :options="options"
            :dynamic-options="dynamicOptions"
            :groups="groups"
            @change="onModalFiltersUpdate"
          />
        </template>

        <template #submit-button="{ filters, dynamicOptions }">
          <BasicButton
            @click="applyModalFilters({ filters, dynamicOptions })"
            appearance="primary"
            full-width
            size="xl"
            >{{ applyFiltersTitle }}</BasicButton
          >
        </template>
      </FiltersModal>
    </TransitionFade>
  </PageSection>
</template>

<script>
import {
  VacanciesService,
  VacanciesSubscriptionsResource,
} from 'src/services/vacancies.js';
import { getHint, markHintAsRead } from 'src/utils/featureHints.js';
import {
  getFormattedNumber,
  getRussianPluralDeclension,
} from 'src/utils/translation.js';

import BasicButton from 'src/components/BasicButton/BasicButton.vue';
import BasicColumns from 'src/components/BasicColumns/BasicColumns.vue';
import BasicLink from 'src/components/BasicLink/BasicLink.vue';
import BasicSlider from 'src/components/BasicSlider/BasicSlider.vue';
import ButtonGroup from 'src/components/ButtonGroup/ButtonGroup.vue';
import CollapsibleSection from 'src/components/CollapsibleSection/CollapsibleSection.vue';
import FiltersControls from 'src/components/filters/FiltersControls/FiltersControls.vue';
import FiltersModal from 'src/components/filters/FiltersModal/FiltersModal.vue';
import SavedFiltersWidget from 'src/components/filters/SavedFiltersWidget/SavedFiltersWidget.vue';
import SearchFilters from 'src/components/filters/SearchFilters/SearchFilters.vue';
import SelectedFilters from 'src/components/filters/SelectedFilters/SelectedFilters.vue';
import BasicSelect from 'src/components/form/BasicSelect/BasicSelect.vue';
import SearchInput from 'src/components/form/SearchInput/SearchInput.vue';
import HotPointHint from 'src/components/HotPointHint/HotPointHint.vue';
import ListPageSidebar from 'src/components/ListPageSidebar/ListPageSidebar.vue';
import NewsSubscribe from 'src/components/NewsSubscribe/NewsSubscribe.vue';
import PageTitle from 'src/components/PageTitle/PageTitle.vue';
import PaginatedResource from 'src/components/pagination/PaginatedResource/PaginatedResource.vue';
import WithPagination from 'src/components/pagination/WithPagination/WithPagination.vue';
import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
import ResourceAction from 'src/components/ResourceAction/ResourceAction.vue';
import SearchPanel from 'src/components/search/SearchPanel/SearchPanel.vue';
import SearchTotal from 'src/components/SearchTotal/SearchTotal.vue';
import SectionBox from 'src/components/SectionBox/SectionBox.vue';
import BasicSection from 'src/components/sections/BasicSection/BasicSection.vue';
import ContentSection from 'src/components/sections/ContentSection/ContentSection.vue';
import PageSection from 'src/components/sections/PageSection/PageSection.vue';
import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';
import SkillBanner from 'src/components/SkillBanner/SkillBanner.vue';
import SocialLinks from 'src/components/SocialLinks/SocialLinks.vue';
import BasicTabs from 'src/components/tabs/BasicTabs/BasicTabs.vue';
import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';
import TransitionFade from 'src/components/transitions/TransitionFade/TransitionFade.vue';
import QuickResponseVacancies from 'src/components/vacancies/QuickResponseVacancies/QuickResponseVacancies.vue';
import VacancyCard from 'src/components/vacancies/VacancyCard/VacancyCard.vue';
import VacancyCardSkeleton from 'src/components/vacancies/VacancyCardSkeleton/VacancyCardSkeleton.vue';
import VacancyFilters from 'src/components/vacancies/VacancyFilters/VacancyFilters.vue';
import VuePortal from 'src/components/VuePortal/VuePortal.vue';

import icon from 'images/no_content.icon.jobs.png';

const VacanciesSubscriptionModal = () =>
  import(
    'src/components/vacancies/VacanciesSubscriptionModal/VacanciesSubscriptionModal.vue'
  );

const vacanciesDeclension = getRussianPluralDeclension(
  'Найдена ${0} вакансия',
  'Найдено ${0} вакансии',
  'Найдено ${0} вакансий',
);

const savedFiltersDescription = `
    Сохраненные фильтры помогают быстрее находить нужные вакансии.
    Чтобы сохранить фильтр, установите нужные параметры поиска и нажмите на кнопку «Сохранить фильтр».
  `;

const submitButtonDeclension = getRussianPluralDeclension(
  'Показать ${0} вакансию',
  'Показать ${0} вакансии',
  'Показать ${0} вакансий',
);

const VACANCY_SUBSCRIPTION_HINT_SLUG = 'vacancy-subscription';

export default {
  name: 'VacancyListPage',
  components: {
    QuickResponseVacancies,
    NoContent,
    VacancyCard,
    VacanciesSubscriptionModal,
    FiltersModal,
    TransitionFade,
    ResourceAction,
    VacancyFilters,
    SavedFiltersWidget,
    CollapsibleSection,
    ListPageSidebar,
    VuePortal,
    VacancyCardSkeleton,
    WithPagination,
    SearchTotal,
    PaginatedResource,
    FiltersControls,
    SelectedFilters,
    SectionGroup,
    SearchFilters,
    TransitionExpand,
    BasicSelect,
    SearchInput,
    SearchPanel,
    BasicTabs,
    ButtonGroup,
    BasicButton,
    PageTitle,
    BasicSection,
    SectionBox,
    PageSection,
    ContentSection,
    BasicLink,
    BasicColumns,
    SocialLinks,
    NewsSubscribe,
    BasicSlider,
    SkillBanner,
    HotPointHint,
  },
  noContent: {
    title: 'Поиск не дал результатов',
    description: 'Попробуйте изменить условия поиска',
    icon: {
      src: icon,
      width: '84px',
      height: '75px',
    },
  },
  savedFiltersDescription,
  inject: [
    'mediaQueries',
    'visitor',
    'filters',
    'suggestedFilters',
    'savedFilters',
  ],
  props: {
    type: String,
    tabs: Array,
    vacancies: Object,
    query: String,
    order: String,
    subscriptions: Array,
    showSeoButton: Boolean,
    showUpdateSeoButton: Boolean,
    ignoreFilterSync: Boolean,
    alias: String,
    headerText: String,
    similarVacancies: Array,
    quickResponseVacancies: Array,
    quickResponseShowFeedback: Boolean,
    groups: Array,
    skillsBanner: Object,
  },
  data() {
    const { vacancies, query, order, subscriptions } = this;

    return {
      state: {
        query,
        order,
        subscriptions,
      },
      currentVacancies: vacancies,
      modalVacancies: null,
      filtersModalVisible: false,
      subscriptionsModalVisible: false,
      shouldIgnoreFilterSync: this.ignoreFilterSync,
      listUpdated: false,
      quickResponseFeedbackEnabled: this.quickResponseShowFeedback,
      quickResponseVacanciesList: this.quickResponseVacancies,
      hint: null,
    };
  },
  mounted() {
    this.checkAndLoadHint();
  },
  methods: {
    action: VacanciesService.getList,
    async checkAndLoadHint() {
      if (this.visitor?.isGuest) return;

      const hint = await getHint(VACANCY_SUBSCRIPTION_HINT_SLUG);

      if (!hint) return;

      const hasSubscriptions = await VacanciesSubscriptionsResource.hasSubscriptions();

      if (hasSubscriptions) {
        markHintAsRead(hint.slug);
        return;
      }

      this.hint = hint;
    },
    markHintAsRead() {
      markHintAsRead(this.hint.slug);
      this.hint = null;
    },
    scheduleListUpdate() {
      // reset custom header text and similar vacancies
      // if filters are applied
      this.listUpdated = true;
      this.shouldIgnoreFilterSync = false;
      this.$nextTick(() => {
        this.$refs.listResource.loadAndReplace(this.params);
      });
    },
    onListRequestSuccess({ data, params }) {
      if (this.shouldIgnoreFilterSync) {
        VacanciesService.syncLocationPageOnly(params.page);
      } else {
        VacanciesService.syncLocation(params);
      }
      this.quickResponseVacanciesList = data.recommendedQuickVacancies;
    },
    onPagination() {
      this.$refs.scrollTarget.$el.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'auto',
      });
    },
    saveFilters(filters) {
      this.savedFilters.save({ ...this.params, ...filters });
    },
    loadSavedFilter({ query, order, filters, filtersData }) {
      if (query) this.state.query = query;
      if (order) this.state.order = order;
      this.filters.set({ filters, dynamicOptions: filtersData });
      this.scheduleListUpdate();
    },
    openModalFilters() {
      this.filtersModalVisible = true;
      this.modalVacancies = this.currentVacancies;
    },
    closeModalFilters() {
      this.$refs.modalFiltersAction.cancel();
      this.filtersModalVisible = false;
    },
    onModalFiltersUpdate(filters) {
      this.shouldIgnoreFilterSync = false;
      this.$nextTick(() => {
        this.$refs.modalFiltersAction.dispatch({
          ...this.baseParams,
          ...filters,
        });
      });
    },
    onModalRequestSuccess({ data }) {
      if (this.filtersModalVisible) {
        this.modalVacancies = data;
      } else {
        this.currentVacancies = data;
      }
      this.quickResponseVacanciesList = data.recommendedQuickVacancies;
    },
    applyModalFilters({ filters, dynamicOptions }) {
      this.filtersModalVisible = false;
      this.filters.set({ filters, dynamicOptions });
      this.currentVacancies = this.modalVacancies;
      const params = { ...this.baseParams, ...filters };
      if (this.shouldIgnoreFilterSync) {
        VacanciesService.syncLocationPageOnly(params.page);
      } else {
        VacanciesService.syncLocation(params);
      }
    },
    onSubscriptionClick() {
      this.subscriptionsModalVisible = true;

      if (this.hint) {
        this.markHintAsRead();
      }
    },
    showSubscribe({ isHr, hasSubscribe }) {
      return !hasSubscribe && !isHr;
    },
    // eslint-disable-next-line complexity
    showQuickResponse(index) {
      return (
        this.quickResponseVacanciesList &&
        this.quickResponseVacanciesList.length &&
        ((index === 0 && this.currentVacancies.list.length === 1) ||
          (index === 1 && this.currentVacancies.list.length > 1))
      );
    },
  },
  computed: {
    baseParams() {
      const { state, type, alias, shouldIgnoreFilterSync } = this;
      const { query, order } = state;
      const ret = { query, order, type };
      if (shouldIgnoreFilterSync && !this.listUpdated) {
        ret.alias = alias;
      }
      return ret;
    },
    seoHeader() {
      return !this.listUpdated && this.headerText;
    },
    similarVacanciesVisible() {
      return (
        !this.listUpdated &&
        this.similarVacancies &&
        this.similarVacancies.length
      );
    },
    params() {
      return { ...this.baseParams, ...this.filters.items };
    },
    rssLink() {
      return VacanciesService.getRssLink(this.params);
    },
    getAddSeoLink() {
      return VacanciesService.getAddSeoLink(this.params);
    },
    getUpdateSeoFilterLink() {
      return VacanciesService.getUpdateSeoFilter(this.alias, this.params);
    },
    searchResultsTitle() {
      const value = getFormattedNumber(this.currentVacancies.meta.totalResults);
      return vacanciesDeclension(value).replace('${0}', value);
    },
    hasCustomizedFilters() {
      return this.filters.selectedList.length !== 0;
    },
    hasSuggestedFilters() {
      return this.suggestedFilters.selectedList.length !== 0;
    },
    hasAnyFilters() {
      return this.hasSuggestedFilters || this.hasCustomizedFilters;
    },
    indicatorAppearance() {
      return this.hasSuggestedFilters && this.hasCustomizedFilters
        ? 'muted'
        : null;
    },
    applyFiltersTitle() {
      if (!this.modalVacancies) return null;
      const value = this.modalVacancies.meta.totalResults;
      return submitButtonDeclension(value).replace('${0}', value);
    },
    mappedParams() {
      if (this.params.alias) return {};
      return VacanciesService.getMappedParams(this.params);
    },
  },
};
</script>
